import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/layouts/DashboardLayout';
import ToastContainer from '../components/ToastContainer';
import DashboardOverviewPrincipal from './principal/DashboardOverview';
import ContentApprovalsTab from './principal/ContentApprovals_New';
import SubmissionReviewsTab from './principal/SubmissionReviews';
import ActivityHistoryTab from './principal/ActivityHistory';
import AdmissionsFeeApprovalsTab from './principal/AdmissionsFeeApprovals';
import '../styles/dashboard.css';
import './principal/PrincipalDashboard.css';

/**
 * PrincipalDashboard Component
 * 
 * Main dashboard for Principal/Content Approver role
 * - Review and approve/reject content changes
 * - Review and approve/reject admissions level fee structures
 * - Review and approve/reject submissions (testimonials, etc)
 * - View approval activity history
 * - Statistics on pending and completed approvals
 * 
 * Theme: Blue/Orange (#2563eb / #f59e0b)
 */
function PrincipalDashboard({ user, onLogout, apiBase }) {
  const toastRef = useRef();
  const [activeTab, setActiveTab] = useState('approvals');
  const [stats, setStats] = useState({
    pendingContent: 0,
    pendingSubmissions: 0,
    pendingFees: 0,
    approvedThisMonth: 0,
    rejectedThisMonth: 0
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadStats();
  }, [refreshTrigger]);

  const loadStats = async () => {
    try {
      const response = await fetch(`${apiBase}/homepage/cms/pending-approvals?limit=1`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (data.ok) {
        setStats(prev => ({
          ...prev,
          pendingContent: data.data?.pagination?.total || 0,
          pendingSubmissions: 0
        }));
      }

      // Fetch pending Admissions Approvals (Fees, Periods, Slots, Promos)
      try {
        const [feeRes, configRes] = await Promise.all([
          fetch(`${apiBase}/admin/admissions/pending-fee-approvals`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          }),
          fetch(`${apiBase}/admin/admissions/config-approvals`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          }),
        ]);
        const feeData = await feeRes.json();
        const configData = await configRes.json();

        let pendingLevels = (feeRes.ok && feeData?.ok) ? (Number(feeData.count) || 0) : 0;
        let pendingPeriods = 0;
        let pendingSlots = 0;
        let pendingPromos = 0;

        if (configRes.ok && configData?.ok && Array.isArray(configData.data)) {
          configData.data.forEach((item) => {
            if (item.config_type === 'period') pendingPeriods++;
            else if (item.config_type === 'test_slot') pendingSlots++;
            else if (item.config_type === 'promo_code') pendingPromos++;
          });
        }

        const totalAdmissionsPending = pendingLevels + pendingPeriods + pendingSlots + pendingPromos;

        setStats(prev => ({
          ...prev,
          pendingFees: totalAdmissionsPending,
          admissionsSubmenuPending: {
            periods: pendingPeriods,
            levels: pendingLevels,
            test_slots: pendingSlots,
            promo_codes: pendingPromos,
            total: totalAdmissionsPending,
          }
        }));
      } catch (_) {}
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const roleColor = '#2563eb';

  return (
    <DashboardLayout
      user={user}
      onLogout={onLogout}
      onTabChange={setActiveTab}
      userRole="principal"
      activeTab={activeTab}
      pendingCount={stats.pendingContent}
      pendingFeesCount={stats.pendingFees}
      admissionsSubmenuPending={stats.admissionsSubmenuPending}
    >
      <div className="principal-content-viewport">
        {activeTab === 'dashboard' && (
          <DashboardOverviewPrincipal 
            apiBase={apiBase} 
            stats={stats} 
            onRefresh={handleRefresh}
            roleColor={roleColor}
          />
        )}

        {(activeTab === 'approvals' || activeTab === 'content-approvals') && (
          <ContentApprovalsTab 
            apiBase={apiBase} 
            roleColor={roleColor}
            onApprovalChange={handleRefresh}
          />
        )}

        {(activeTab === 'admissions-periods' || activeTab === 'admissions-overview') && (
          <AdmissionsFeeApprovalsTab
            apiBase={apiBase}
            roleColor={roleColor}
            initialCategory="periods"
            onFeeChange={handleRefresh}
          />
        )}

        {activeTab === 'admissions-fee-approvals' && (
          <AdmissionsFeeApprovalsTab
            apiBase={apiBase}
            roleColor={roleColor}
            initialCategory="levels"
            onFeeChange={handleRefresh}
          />
        )}

        {activeTab === 'admissions-test-slots' && (
          <AdmissionsFeeApprovalsTab
            apiBase={apiBase}
            roleColor={roleColor}
            initialCategory="test_slots"
            onFeeChange={handleRefresh}
          />
        )}

        {activeTab === 'admissions-promo-codes' && (
          <AdmissionsFeeApprovalsTab
            apiBase={apiBase}
            roleColor={roleColor}
            initialCategory="promo_codes"
            onFeeChange={handleRefresh}
          />
        )}

        {(activeTab === 'submissions' || activeTab === 'submission-reviews') && (
          <SubmissionReviewsTab 
            apiBase={apiBase} 
            roleColor={roleColor}
            onReviewChange={handleRefresh}
          />
        )}

        {activeTab === 'activity-history' && (
          <ActivityHistoryTab 
            apiBase={apiBase} 
            roleColor={roleColor}
          />
        )}
      </div>

      {/* Toast Notification System */}
      <ToastContainer ref={toastRef} />
    </DashboardLayout>
  );
}

export default PrincipalDashboard;
